// physicalSoundSynthesis.ts: Physically-based sound synthesis engine for characteristic realism
// All sounds in NeuroPsyMap must use physical modeling for realistic characteristics

import { NeuralNetworkSimulator } from './NeuralNetworkSimulator';

/**
 * Physical Sound Synthesis Engine
 * 
 * This module provides physically-based sound synthesis for all audio in NeuroPsyMap.
 * All sounds are generated using physical models that capture the actual characteristics
 * of the sound-producing mechanisms.
 */

export interface TurbulentFlowParams {
    flowRate: number;           // m^3/s - actual airflow rate
    constriction: number;      // 0-1 - airway constriction (affects turbulence)
    airPressure: number;       // Pa - air pressure behind constriction
    temperature: number;       // K - air temperature (affects speed of sound)
}

export interface VocalTractParams {
    // Formant frequencies (Hz) - three main resonances of vocal tract
    formant1: number;          // F1 - primary resonance (typically 500-1200 Hz)
    formant2: number;          // F2 - secondary resonance (typically 1000-2500 Hz)
    formant3: number;          // F3 - tertiary resonance (typically 2000-3500 Hz)
    
    // Formant bandwidths (Hz) - how sharp the resonances are
    bandwidth1: number;
    bandwidth2: number;
    bandwidth3: number;
    
    // Dynamic parameters
    glottalGain: number;       // 0-1 - vocal fold tension
    breathiness: number;       // 0-1 - amount of breath noise vs. tone
    
    // Enhanced features
    previousFormants?: { f1: number; f2: number; f3: number };  // Coarticulation
    nextFormants?: { f1: number; f2: number; f3: number };
    coarticulationStrength?: number;  // 0-1
    isNasal?: boolean;         // Nasal coupling
    nasalCoupling?: number;    // 0-1
    useLFModel?: boolean;      // Use LF glottal pulse
}

export interface LFGlottalParams {
    E0: number;        // Maximum amplitude
    F0: number;        // Fundamental frequency (Hz)
    T0: number;        // Fundamental period (s)
    Te: number;        // Time to maximum (0.25 × T0)
    Tp: number;        // Pulse end time (0.5 × T0)
}

export interface ConsonantSynthesisParams {
    type: 'stop' | 'fricative' | 'nasal' | 'approximant';
    place: string;     // Place of articulation
    voicing: 'voiced' | 'voiceless';
    
    // Stop-specific
    burstAmplitude?: number;
    VOT?: number;      // Voice Onset Time (ms)
    
    // Fricative-specific
    noiseAmplitude?: number;
    spectralTilt?: number;  // -1 to 1
}

export interface ModalResonatorParams {
    frequency: number;         // Hz - natural resonance frequency
    amplitude: number;         // 0-1 - excitation amplitude
    damping: number;          // 0-1 - rate of decay (higher = faster decay)
    bandwidth: number;        // Hz - resonance bandwidth
}

export interface TurbulentNoiseSource {
    generateSample(dt: number, params: TurbulentFlowParams): number;
    getSpectralCharacteristics(params: TurbulentFlowParams): { peakFreq: number; bandwidth: number; spectralSlope: number };
}

export interface FormantFilter {
    process(sample: number, params: VocalTractParams): number;
    updateParams(params: VocalTractParams): void;
}

/** Internal IIR filter state for formant processing */
interface FilterState {
    a1: number; a2: number;
    x1: number; x2: number;
    y1: number; y2: number;
}

export class PhysicalSoundSynthesizer {
    private audioContext: AudioContext | null;
    private nodeNetwork: NeuralNetworkSimulator | undefined;
    
    // Physical models
    private turbulentNoise: TurbulentNoiseSource;
    private formantFilter: FormantFilter;
    
    constructor(audioContext: AudioContext | null, network?: NeuralNetworkSimulator) {
        this.audioContext = audioContext;
        this.nodeNetwork = network;
        this.turbulentNoise = new TurbulentAirflowModel();
        
        // Initialize formant filter with sample rate
        const sampleRate = audioContext?.sampleRate || 44100;
        this.formantFilter = new MultipleFormantFilter(sampleRate);
    }
    
    /**
     * Generate breathing sound using physical modeling
     * 
     * Models: Turbulent airflow through vocal tract with proper spectral characteristics
     * Physical basis: Air rushing through constricted passages (nose, mouth, throat)
     * 
     * @param phase - Current breathing phase
     * @param breathDepth - Depth of breath (0-1)
     * @param duration - Sound duration in seconds
     */
    synthesizeBreathingSound(
        phase: 'inhale' | 'exhale' | 'pause_inhale' | 'pause_exhale',
        breathDepth: number,
        duration: number
    ): AudioBuffer {
        if (!this.audioContext) throw new Error('AudioContext not available');
        
        const sampleRate = this.audioContext.sampleRate;
        const bufferLength = Math.floor(sampleRate * duration);
        const buffer = this.audioContext.createBuffer(1, bufferLength, sampleRate);
        const data = buffer.getChannelData(0);
        
        // Physical parameters based on breathing phase
        const flowParams = this.getBreathingFlowParams(phase, breathDepth);
        const formantParams = this.getBreathingFormantParams(phase, breathDepth);
        
        // Update formant filter parameters
        this.formantFilter.updateParams(formantParams);
        
        const dt = 1 / sampleRate;
        const spectralChars = this.turbulentNoise.getSpectralCharacteristics(flowParams);
        
        // Generate physically-modeled breathing sound
        for (let i = 0; i < bufferLength; i++) {
            const t = i * dt;
            
            // Generate turbulent noise with physical characteristics
            let sample = this.turbulentNoise.generateSample(dt, flowParams);
            
            // Apply formant filtering (vocal tract resonance)
            sample = this.formantFilter.process(sample, formantParams);
            
            // Apply amplitude envelope based on breath phase dynamics
            const envelope = this.getBreathingEnvelope(t, duration, phase, breathDepth);
            sample *= envelope;
            
            // Normalize to prevent clipping
            sample = Math.max(-0.95, Math.min(0.95, sample));
            
            data[i] = sample;
        }
        
        return buffer;
    }
    
    /**
     * Synthesize discovery sound using physical modeling
     * 
     * Models: Modal resonance of excited structure
     * Physical basis: Natural frequencies of vibrating element
     * 
     * @param frequency - Base frequency of excitation
     * @param duration - Sound duration in seconds
     * @param excitement - Level of excitement (affects harmonics and energy)
     */
    synthesizeDiscoverySound(
        frequency: number,
        duration: number,
        excitement: number
    ): AudioBuffer {
        if (!this.audioContext) throw new Error('AudioContext not available');
        
        const sampleRate = this.audioContext.sampleRate;
        const bufferLength = Math.floor(sampleRate * duration);
        const buffer = this.audioContext.createBuffer(1, bufferLength, sampleRate);
        const data = buffer.getChannelData(0);
        
        // Physical modal parameters
        const modes = this.calculateModalFrequencies(frequency, excitement);
        const dt = 1 / sampleRate;
        
        // Generate modal synthesis
        for (let i = 0; i < bufferLength; i++) {
            const t = i * dt;
            let sample = 0;
            
            // Sum modal contributions
            for (const mode of modes) {
                const amplitude = mode.amplitude * Math.exp(-mode.damping * t * Math.PI * 2);
                const phase = mode.frequency * t * Math.PI * 2;
                
                // Add harmonic content based on mode
                sample += amplitude * Math.sin(phase);
                
                // Add overtones for richer sound
                if (mode.overtoneGain) {
                    sample += amplitude * mode.overtoneGain * Math.sin(phase * 2);
                }
            }
            
            // Apply excitation envelope
            const envelope = this.getExcitationEnvelope(t, duration, excitement);
            sample *= envelope;
            
            // Normalize
            sample = Math.max(-0.95, Math.min(0.95, sample));
            
            data[i] = sample;
        }
        
        return buffer;
    }
    
    /**
     * Synthesize voice-like sound with formant synthesis
     * 
     * Models: Vocal tract formants with proper spectral shaping
     * Physical basis: Resonance of vocal tract cavities
     * 
     * @param pitch - Pitch in Hz
     * @param formants - Formant frequencies
     * @param duration - Sound duration in seconds
     * @param useLFModel - Use LF glottal pulse model (default: true)
     */
    synthesizeFormantVoice(
        pitch: number,
        formants: { f1: number; f2: number; f3: number },
        duration: number,
        useLFModel: boolean = true
    ): AudioBuffer {
        if (!this.audioContext) throw new Error('AudioContext not available');
        
        const sampleRate = this.audioContext.sampleRate;
        const bufferLength = Math.floor(sampleRate * duration);
        const buffer = this.audioContext.createBuffer(1, bufferLength, sampleRate);
        const data = buffer.getChannelData(0);
        
        const formantParams: VocalTractParams = {
            formant1: formants.f1,
            formant2: formants.f2,
            formant3: formants.f3,
            bandwidth1: 50,
            bandwidth2: 70,
            bandwidth3: 100,
            glottalGain: 0.7,
            breathiness: 0.3,
            useLFModel: useLFModel
        };
        
        // Update formant filter
        this.formantFilter.updateParams(formantParams);
        
        const dt = 1 / sampleRate;
        
        // Generate voice sound
        for (let i = 0; i < bufferLength; i++) {
            const t = i * dt;
            
            // Generate source signal
            let source = 0;
            
            if (useLFModel) {
                // Use LF glottal pulse model for more realistic source
                source = LFGlottalPulseModel.generateSample(t, pitch, 1.0);
                
                // Add harmonics with proper decay
                const numHarmonics = 10;
                for (let h = 2; h <= numHarmonics; h++) {
                    const harmonicFreq = pitch * h;
                    const harmonicAmp = (1 / h) * Math.exp(-h * 0.3);
                    source += harmonicAmp * Math.sin(harmonicFreq * t * Math.PI * 2);
                }
            } else {
                // Fallback to simple harmonic synthesis
                const numHarmonics = 10;
                for (let h = 1; h <= numHarmonics; h++) {
                    const harmonicFreq = pitch * h;
                    const harmonicAmp = 1 / h; // Natural harmonic decay
                    source += harmonicAmp * Math.sin(harmonicFreq * t * Math.PI * 2);
                }
            }
            
            // Add breath noise
            source += this.turbulentNoise.generateSample(dt, {
                flowRate: 0.001,
                constriction: 0.3,
                airPressure: 101325,
                temperature: 310
            }) * formantParams.breathiness! * 0.1;
            
            // Apply formant filtering
            let sample = this.formantFilter.process(source, formantParams);
            
            // Apply voice envelope
            const envelope = this.getVoiceEnvelope(t, duration);
            sample *= envelope;
            
            // Normalize
            sample = Math.max(-0.95, Math.min(0.95, sample));
            
            data[i] = sample;
        }
        
        return buffer;
    }
    
    // Helper methods for physical parameters
    
    private getBreathingFlowParams(phase: string, depth: number): TurbulentFlowParams {
        // Physical airflow characteristics for each breathing phase
        
        switch (phase) {
            case 'inhale':
                return {
                    flowRate: 0.001 * depth,      // m^3/s - proportional to depth
                    constriction: 0.4,            // Moderate constriction (nose/mouth)
                    airPressure: 101325 * (1 - depth * 0.05), // Pressure drop during inhale
                    temperature: 295              // Room temperature air
                };
                
            case 'exhale':
                return {
                    flowRate: 0.0012 * depth,     // Faster exhale
                    constriction: 0.5,            // More constriction (vocal tract)
                    airPressure: 101325 * (1 + depth * 0.08), // Pressure buildup
                    temperature: 310              // Body temperature air
                };
                
            case 'pause_inhale':
                return {
                    flowRate: 0.0001 * depth,     // Very low flow
                    constriction: 0.2,            // Minimal constriction
                    airPressure: 101325,
                    temperature: 295
                };
                
            case 'pause_exhale':
                return {
                    flowRate: 0.00008 * depth,    // Minimal flow
                    constriction: 0.15,
                    airPressure: 101325,
                    temperature: 310
                };
                
            default:
                return {
                    flowRate: 0.001,
                    constriction: 0.4,
                    airPressure: 101325,
                    temperature: 303
                };
        }
    }
    
    private getBreathingFormantParams(phase: string, depth: number): VocalTractParams {
        // Vocal tract formants based on breathing phase
        
        switch (phase) {
            case 'inhale':
                return {
                    formant1: 150 + depth * 50,   // Lower frequencies for inhale
                    formant2: 500 + depth * 200,
                    formant3: 1500 + depth * 500,
                    bandwidth1: 80,
                    bandwidth2: 100,
                    bandwidth3: 120,
                    glottalGain: 0.3,
                    breathiness: 0.9
                };
                
            case 'exhale':
                return {
                    formant1: 300 + depth * 100,  // Higher frequencies for exhale
                    formant2: 1200 + depth * 300,
                    formant3: 2500 + depth * 500,
                    bandwidth1: 100,
                    bandwidth2: 150,
                    bandwidth3: 180,
                    glottalGain: 0.5,
                    breathiness: 0.7
                };
                
            default: // pauses
                return {
                    formant1: 100 + depth * 30,
                    formant2: 400 + depth * 100,
                    formant3: 1200 + depth * 300,
                    bandwidth1: 60,
                    bandwidth2: 80,
                    bandwidth3: 100,
                    glottalGain: 0.1,
                    breathiness: 0.95
                };
        }
    }
    
    private getBreathingEnvelope(t: number, duration: number, phase: string, depth: number): number {
        // Physical envelope based on breath dynamics
        const progress = t / duration;
        
        switch (phase) {
            case 'inhale':
                // Inhale: gradual rise, sustained, then quick drop
                if (progress < 0.3) return progress / 0.3;
                if (progress < 0.8) return 0.8 + (depth * 0.2);
                return (1 - progress) / 0.2 * (0.8 + depth * 0.2);
                
            case 'exhale':
                // Exhale: quick rise, gradual decay
                if (progress < 0.2) return progress / 0.2;
                return Math.exp(-(progress - 0.2) * 3 * (1 - depth));
                
            default: // pauses
                return Math.exp(-progress * 5); // Quick fade
        }
    }
    
    private calculateModalFrequencies(baseFreq: number, excitement: number): Array<ModalResonatorParams & { overtoneGain: number }> {
        // Calculate physical modal frequencies with proper harmonic relationships
        const modes: Array<ModalResonatorParams & { overtoneGain: number }> = [];
        
        // Base frequency
        modes.push({
            frequency: baseFreq,
            amplitude: 1.0,
            damping: 0.5 + (1 - excitement) * 0.5, // More damping when less excited
            bandwidth: baseFreq * 0.1,
            overtoneGain: 0
        });
        
        // First harmonic (2x frequency)
        modes.push({
            frequency: baseFreq * 2,
            amplitude: 0.5 * excitement, // Stronger when excited
            damping: 0.4 + (1 - excitement) * 0.4,
            bandwidth: baseFreq * 0.15,
            overtoneGain: 0.3
        });
        
        // Second harmonic (3x frequency)
        if (excitement > 0.5) {
            modes.push({
                frequency: baseFreq * 3,
                amplitude: 0.3 * excitement,
                damping: 0.6,
                bandwidth: baseFreq * 0.2,
                overtoneGain: 0.2
            });
        }
        
        // Third harmonic (5x frequency) - only at high excitement
        if (excitement > 0.7) {
            modes.push({
                frequency: baseFreq * 5,
                amplitude: 0.15 * excitement,
                damping: 0.8,
                bandwidth: baseFreq * 0.25,
                overtoneGain: 0.1
            });
        }
        
        return modes;
    }
    
    private getExcitationEnvelope(t: number, duration: number, excitement: number): number {
        // Physical excitation envelope - sharp attack, exponential decay
        const progress = t / duration;
        
        if (progress < 0.05) {
            // Very fast attack (5% of duration)
            return progress / 0.05;
        }
        
        // Exponential decay
        const decayStart = 0.05;
        const decayRate = 2 + (1 - excitement) * 3; // Faster decay when less excited
        return Math.exp(-(progress - decayStart) * decayRate / (1 - decayStart));
    }
    
    private getVoiceEnvelope(t: number, duration: number): number {
        // ADSR envelope for voice
        const progress = t / duration;
        
        const attack = 0.1;
        const decay = 0.2;
        const sustain = 0.7;
        const release = 0.2;
        
        if (progress < attack) {
            // Attack phase
            return progress / attack;
        } else if (progress < attack + decay) {
            // Decay phase
            const decayProgress = (progress - attack) / decay;
            return 1 - decayProgress * (1 - sustain);
        } else if (progress < 1 - release) {
            // Sustain phase
            return sustain;
        } else {
            // Release phase
            const releaseProgress = (progress - (1 - release)) / release;
            return sustain * (1 - releaseProgress);
        }
    }
    
    /**
     * Synthesize musical note with formant synthesis for vowel sounds
     * Enhanced with LF glottal pulse model
     */
    synthesizeMusicalNote(pitch: number, vowel: string, duration: number, velocity: number): AudioBuffer {
        if (!this.audioContext) throw new Error('AudioContext not available');
        
        const formants = this.getVowelFormants(vowel, pitch);
        const sampleRate = this.audioContext.sampleRate;
        const bufferLength = Math.floor(sampleRate * duration);
        const buffer = this.audioContext.createBuffer(1, bufferLength, sampleRate);
        const data = buffer.getChannelData(0);
        
        const formantParams: VocalTractParams = {
            formant1: formants.f1,
            formant2: formants.f2,
            formant3: formants.f3,
            bandwidth1: 50,
            bandwidth2: 70,
            bandwidth3: 100,
            glottalGain: 0.6,
            breathiness: 0.2 * velocity,
            useLFModel: true  // Use LF glottal pulse model
        };
        
        this.formantFilter.updateParams(formantParams);
        const dt = 1 / sampleRate;
        
        for (let i = 0; i < bufferLength; i++) {
            const t = i * dt;
            let source = 0;
            
            // Use LF glottal pulse as base
            source = LFGlottalPulseModel.generateSample(t, pitch, 1.0);
            
            // Add harmonics with proper decay
            const numHarmonics = 10;
            for (let h = 2; h <= numHarmonics; h++) {
                const harmonicFreq = pitch * h;
                const harmonicAmp = (1 / h) * Math.exp(-h * 0.3);
                source += harmonicAmp * Math.sin(harmonicFreq * t * Math.PI * 2);
            }
            
            // Add breath noise based on velocity
            source += this.turbulentNoise.generateSample(dt, {
                flowRate: 0.001 * velocity,
                constriction: 0.4,
                airPressure: 101325,
                temperature: 310
            }) * formantParams.breathiness! * 0.1;
            
            let sample = this.formantFilter.process(source, formantParams);
            const envelope = this.getMusicalEnvelope(t, duration, velocity);
            sample *= envelope;
            sample = Math.max(-0.95, Math.min(0.95, sample));
            data[i] = sample;
        }
        
        return buffer;
    }
    
    /**
     * Synthesize complete syllable with consonant + vowel
     * 
     * @param syllable - Syllable structure: { consonant?: string, vowel: string }
     * @param pitch - Base pitch in Hz
     * @param duration - Total duration in seconds
     * @param velocity - Note velocity (0-1)
     */
    synthesizeSyllable(
        syllable: { consonant?: string; vowel: string },
        pitch: number,
        duration: number,
        velocity: number
    ): AudioBuffer {
        if (!this.audioContext) throw new Error('AudioContext not available');
        
        const sampleRate = this.audioContext.sampleRate;
        
        // Allocate durations: 30% to consonant (if present), 70% to vowel
        const consonantDuration = syllable.consonant ? duration * 0.3 : 0;
        const vowelDuration = duration - consonantDuration;
        
        // Synthesize consonant (if present)
        let consonantBuffer: Float32Array | null = null;
        if (syllable.consonant) {
            consonantBuffer = this.synthesizeConsonantByName(
                syllable.consonant,
                consonantDuration,
                sampleRate
            );
        }
        
        // Synthesize vowel
        const vowelBuffer = this.synthesizeMusicalNote(
            pitch,
            syllable.vowel,
            vowelDuration,
            velocity
        );
        
        // Combine buffers
        const totalLength = Math.floor(sampleRate * duration);
        const combinedBuffer = this.audioContext.createBuffer(1, totalLength, sampleRate);
        const data = combinedBuffer.getChannelData(0);
        
        if (consonantBuffer) {
            // Copy consonant
            for (let i = 0; i < consonantBuffer.length && i < totalLength; i++) {
                data[i] = consonantBuffer[i];
            }
            
            // Copy vowel with crossfade
            const vowelData = vowelBuffer.getChannelData(0);
            const crossfadeSamples = Math.floor(sampleRate * 0.01); // 10ms crossfade
            
            for (let i = 0; i < vowelData.length && i + consonantBuffer.length < totalLength; i++) {
                const idx = i + consonantBuffer.length;
                const fade = i < crossfadeSamples ? i / crossfadeSamples : 1;
                
                if (idx < consonantBuffer.length) {
                    // Crossfade region
                    data[idx] = data[idx] * (1 - fade) + vowelData[i] * fade;
                } else {
                    data[idx] = vowelData[i];
                }
            }
        } else {
            // No consonant, just copy vowel
            const vowelData = vowelBuffer.getChannelData(0);
            for (let i = 0; i < vowelData.length && i < totalLength; i++) {
                data[i] = vowelData[i];
            }
        }
        
        return combinedBuffer;
    }
    
    /**
     * Synthesize consonant by name (maps IPA-like symbols to physical models)
     */
    private synthesizeConsonantByName(
        consonant: string,
        duration: number,
        sampleRate: number
    ): Float32Array {
        // Map consonant symbols to synthesis parameters
        const consonantMap: Record<string, { type: 'stop' | 'fricative' | 'nasal'; place: string; voicing: 'voiced' | 'voiceless' }> = {
            // Stops
            'p': { type: 'stop', place: 'bilabial', voicing: 'voiceless' },
            'b': { type: 'stop', place: 'bilabial', voicing: 'voiced' },
            't': { type: 'stop', place: 'alveolar', voicing: 'voiceless' },
            'd': { type: 'stop', place: 'alveolar', voicing: 'voiced' },
            'k': { type: 'stop', place: 'velar', voicing: 'voiceless' },
            'g': { type: 'stop', place: 'velar', voicing: 'voiced' },
            
            // Fricatives
            'f': { type: 'fricative', place: 'labiodental', voicing: 'voiceless' },
            'v': { type: 'fricative', place: 'labiodental', voicing: 'voiced' },
            'θ': { type: 'fricative', place: 'dental', voicing: 'voiceless' },
            'ð': { type: 'fricative', place: 'dental', voicing: 'voiced' },
            's': { type: 'fricative', place: 'alveolar', voicing: 'voiceless' },
            'z': { type: 'fricative', place: 'alveolar', voicing: 'voiced' },
            'ʃ': { type: 'fricative', place: 'postalveolar', voicing: 'voiceless' },
            'ʒ': { type: 'fricative', place: 'postalveolar', voicing: 'voiced' },
            
            // Nasals
            'm': { type: 'nasal', place: 'bilabial', voicing: 'voiced' },
            'n': { type: 'nasal', place: 'alveolar', voicing: 'voiced' },
            'ŋ': { type: 'nasal', place: 'velar', voicing: 'voiced' }
        };
        
        const params = consonantMap[consonant.toLowerCase()];
        
        if (!params) {
            // Default to neutral fricative
            return ConsonantSynthesizer.synthesizeFricative('alveolar', 'voiceless', duration, sampleRate);
        }
        
        switch (params.type) {
            case 'stop':
                return ConsonantSynthesizer.synthesizeStop(params.place, params.voicing, duration, sampleRate);
            case 'fricative':
                return ConsonantSynthesizer.synthesizeFricative(params.place, params.voicing, duration, sampleRate);
            case 'nasal':
                return ConsonantSynthesizer.synthesizeNasal(params.place, duration, sampleRate);
            default:
                return ConsonantSynthesizer.synthesizeFricative('alveolar', 'voiceless', duration, sampleRate);
        }
    }
    
    /**
     * Synthesize UI feedback sound using modal synthesis
     */
    synthesizeUIInteraction(baseFrequency: number, duration: number, energy: number, objectType: 'buzz' | 'sparkle'): AudioBuffer {
        if (!this.audioContext) throw new Error('AudioContext not available');
        
        const sampleRate = this.audioContext.sampleRate;
        const bufferLength = Math.floor(sampleRate * duration);
        const buffer = this.audioContext.createBuffer(1, bufferLength, sampleRate);
        const data = buffer.getChannelData(0);
        
        const modes = this.calculateModalFrequencies(baseFrequency, energy);
        
        if (objectType === 'sparkle') {
            modes.push(...modes.map(m => ({
                frequency: m.frequency * 2,
                amplitude: m.amplitude * 0.5,
                damping: m.damping,
                bandwidth: m.bandwidth,
                overtoneGain: m.overtoneGain
            })));
        }
        
        const dt = 1 / sampleRate;
        
        for (let i = 0; i < bufferLength; i++) {
            const t = i * dt;
            let sample = 0;
            
            for (const mode of modes) {
                const amplitude = mode.amplitude * Math.exp(-mode.damping * t * Math.PI * 2);
                const phase = mode.frequency * t * Math.PI * 2;
                sample += amplitude * Math.sin(phase);
                
                if (mode.overtoneGain && energy > 0.5) {
                    sample += amplitude * mode.overtoneGain * Math.sin(phase * 2);
                }
            }
            
            const envelope = this.getExcitationEnvelope(t, duration, energy);
            sample *= envelope;
            sample = Math.max(-0.95, Math.min(0.95, sample));
            data[i] = sample;
        }
        
        return buffer;
    }
    
    /**
     * Synthesize neural connection impulse sound using spike-train excitation
     * Physical basis: brief neural spikes exciting a damped resonator + synaptic noise
     */
    synthesizeNeuralImpulseConnection(
        spikeRateHz: number,
        duration: number,
        intensity: number
    ): AudioBuffer {
        if (!this.audioContext) throw new Error('AudioContext not available');
        const sampleRate = this.audioContext.sampleRate;
        const bufferLength = Math.floor(sampleRate * duration);
        const buffer = this.audioContext.createBuffer(1, bufferLength, sampleRate);
        const data = buffer.getChannelData(0);

        // Resonator parameters approximating synaptic membrane/axon bundle resonance
        const baseFreq = 800; // Hz
        const damping = 0.9;  // Heavier damping for quick decay
        const modes = [
            { frequency: baseFreq, amplitude: 1.0 * intensity, damping: 1.2, bandwidth: baseFreq * 0.1, overtoneGain: 0 },
            { frequency: baseFreq * 2.2, amplitude: 0.3 * intensity, damping: 1.6, bandwidth: baseFreq * 0.15, overtoneGain: 0 },
        ];

        // Generate Poisson spike train
        const dt = 1 / sampleRate;
        let nextSpikeTime = 0;
        const spikes: number[] = [];
        const lambda = spikeRateHz; // spikes per second

        while (nextSpikeTime < duration) {
            const u = Math.random();
            const isi = -Math.log(1 - u) / lambda; // exponential inter-spike interval
            nextSpikeTime += isi;
            if (nextSpikeTime < duration) spikes.push(Math.floor(nextSpikeTime * sampleRate));
        }

        // Excite resonator at spike times (as short impulses filtered by modes)
        for (let i = 0; i < bufferLength; i++) {
            let sample = 0;

            // Sum modal responses
            const t = i * dt;
            for (const mode of modes) {
                const env = Math.exp(-mode.damping * t * Math.PI * 2);
                sample += mode.amplitude * env * Math.sin(mode.frequency * t * Math.PI * 2);
            }

            // Add spike impulses (delta approximated by short pulse shaped by quick decay)
            // Check if current index is near a spike
            // Use 1 ms pulse window
            const pulseWindow = Math.floor(sampleRate * 0.001);
            for (const spikeIdx of spikes) {
                const d = i - spikeIdx;
                if (d >= 0 && d < pulseWindow) {
                    const pulse = Math.exp(-d / (pulseWindow * 0.3));
                    sample += pulse * 0.5 * intensity;
                }
            }

            // Add synaptic/turbulent noise
            sample += this.turbulentNoise.generateSample(dt, {
                flowRate: 0.0002 * intensity,
                constriction: 0.3,
                airPressure: 101325,
                temperature: 310
            }) * 0.05;

            // Normalize
            data[i] = Math.max(-0.95, Math.min(0.95, sample));
        }

        return buffer;
    }

    /**
     * Synthesize cardiac sound (heart hum) using cardiomechanical model
     */
    synthesizeCardiacSound(baseFrequency: number, duration: number, intensity: number): AudioBuffer {
        if (!this.audioContext) throw new Error('AudioContext not available');
        
        const sampleRate = this.audioContext.sampleRate;
        const bufferLength = Math.floor(sampleRate * duration);
        const buffer = this.audioContext.createBuffer(1, bufferLength, sampleRate);
        const data = buffer.getChannelData(0);
        
        const dt = 1 / sampleRate;
        
        for (let i = 0; i < bufferLength; i++) {
            const t = i * dt;
            let sample = 0;
            
            const lubFreq = 40 + Math.random() * 15;
            const lubPhase = lubFreq * t * Math.PI * 2;
            const lubDecay = Math.exp(-t * 8);
            sample += 0.3 * Math.sin(lubPhase) * lubDecay * intensity;
            
            const dubFreq = 50 + Math.random() * 15;
            const dubPhase = dubFreq * t * Math.PI * 2;
            const dubDecay = Math.exp(-t * 6);
            sample += 0.25 * Math.sin(dubPhase) * dubDecay * intensity;
            
            const murmur = this.turbulentNoise.generateSample(dt, {
                flowRate: 0.0005 * intensity,
                constriction: 0.2,
                airPressure: 13300,
                temperature: 310
            }) * 0.1 * intensity;
            
            sample += murmur;
            
            const chamberFreq = 25;
            const chamberPhase = chamberFreq * t * Math.PI * 2;
            const chamberDecay = Math.exp(-t * 4);
            sample += 0.15 * Math.sin(chamberPhase) * chamberDecay * intensity;
            
            sample = Math.max(-0.95, Math.min(0.95, sample));
            data[i] = sample;
        }
        
        return buffer;
    }
    
    private getVowelFormants(vowel: string, pitch: number): { f1: number; f2: number; f3: number } {
        const baseFormants: Record<string, { f1: number; f2: number; f3: number }> = {
            'a': { f1: 700, f2: 1220, f3: 2600 },
            'e': { f1: 500, f2: 1790, f3: 2600 },
            'i': { f1: 300, f2: 2290, f3: 3010 },
            'o': { f1: 500, f2: 870, f3: 2240 },
            'u': { f1: 320, f2: 600, f3: 2150 },
            'ae': { f1: 900, f2: 1660, f3: 2600 },
            'ou': { f1: 550, f2: 1020, f3: 2400 }
        };
        
        const formants = baseFormants[vowel.toLowerCase()] || baseFormants['a'];
        const pitchAdjustment = (pitch - 261.6) / 261.6 * 0.1;
        
        return {
            f1: formants.f1 * (1 + pitchAdjustment * 0.3),
            f2: formants.f2 * (1 + pitchAdjustment * 0.2),
            f3: formants.f3 * (1 + pitchAdjustment * 0.15)
        };
    }
    
    private getMusicalEnvelope(t: number, duration: number, velocity: number): number {
        const attack = 0.01 + (1 - velocity) * 0.02;
        const decay = 0.05;
        const sustain = 0.6 + velocity * 0.3;
        const release = 0.08 + velocity * 0.02;
        
        const progress = t / duration;
        
        if (progress < attack) {
            return progress / attack;
        } else if (progress < attack + decay) {
            const decayProgress = (progress - attack) / decay;
            return 1 - decayProgress * (1 - sustain);
        } else if (progress < 1 - release) {
            return sustain;
        } else {
            const releaseProgress = (progress - (1 - release)) / release;
            return sustain * (1 - releaseProgress);
        }
    }
}

/**
 * Turbulent Airflow Model
 * 
 * Physically models turbulent airflow through constricted passages.
 * Based on proper spectral characteristics of turbulent flow.
 */
class TurbulentAirflowModel implements TurbulentNoiseSource {
    private lastSample = 0;
    private filterState = 0;
    
    generateSample(dt: number, params: TurbulentFlowParams): number {
        // Generate turbulent noise with proper spectral characteristics
        // Turbulence has 1/f^alpha spectral density (alpha typically ~1.5-2.5)
        
        // Generate white noise
        const whiteNoise = (Math.random() * 2 - 1);
        
        // Apply spectral shaping based on flow parameters
        const spectralSlope = this.getSpectralCharacteristics(params).spectralSlope;
        
        // Use simple IIR filter to approximate 1/f spectrum
        const alpha = 0.5; // Weight for filter feedback
        this.filterState = alpha * this.filterState + (1 - alpha) * whiteNoise;
        
        // Add flow-dependent modulation
        const flowModulation = Math.sin(params.flowRate * 1000 * dt * Math.PI * 2) * params.constriction;
        
        return (this.filterState + whiteNoise * 0.3) * (1 + flowModulation) * params.flowRate * 1000;
    }
    
    getSpectralCharacteristics(params: TurbulentFlowParams): { peakFreq: number; bandwidth: number; spectralSlope: number } {
        // Calculate spectral characteristics based on flow physics
        
        // Peak frequency related to flow velocity
        const velocity = params.flowRate / (Math.PI * 0.001 * 0.001); // Approximate area
        const peakFreq = velocity * params.constriction * 50; // Strouhal number approximation
        
        // Bandwidth related to constriction
        const bandwidth = peakFreq * (0.3 + params.constriction * 0.5);
        
        // Spectral slope of turbulent flow (typically 1.5-2.5)
        const spectralSlope = 1.5 + params.constriction * 1.0;
        
        return { peakFreq, bandwidth, spectralSlope };
    }
}

/**
 * Multiple Formant Filter
 * 
 * Implements formant filtering using cascaded second-order sections.
 * Each formant is modeled as a resonant bandpass filter.
 */
class MultipleFormantFilter implements FormantFilter {
    private filterStates: Array<{ x1: number; x2: number; y1: number; y2: number; a1: number; a2: number; sampleRate: number }> = [];
    private currentParams: VocalTractParams | null = null;
    private sampleRate: number;
    
    constructor(sampleRate: number = 44100) {
        this.sampleRate = sampleRate;
    }
    
    process(sample: number, params: VocalTractParams): number {
        // Apply cascade of formant filters
        if (!this.currentParams || this.currentParams.formant1 !== params.formant1) {
            this.updateParams(params);
        }
        
        let output = sample;
        
        // Process through each formant filter
        for (const state of this.filterStates) {
            output = this.processFormantFilter(output, state);
        }
        
        // Apply glottal gain
        return output * params.glottalGain;
    }
    
    updateParams(params: VocalTractParams): void {
        this.currentParams = params;
        this.filterStates = [];
        
        // Create filter state for each formant with proper IIR coefficients
        this.filterStates.push(this.createFormantFilterState(params.formant1, params.bandwidth1, this.sampleRate));
        this.filterStates.push(this.createFormantFilterState(params.formant2, params.bandwidth2, this.sampleRate));
        this.filterStates.push(this.createFormantFilterState(params.formant3, params.bandwidth3, this.sampleRate));
    }
    
    // SCIENTIFIC MODEL: IIR Formant Filter
    // H(z) = b_i / (1 - a_1i*z^(-1) - a_2i*z^(-2))
    // where for bandpass: 
    // a1 = -2*r*cos(θ), a2 = r²
    // r = exp(-π*B/fs), θ = 2π*f/fs
    private createFormantFilterState(frequency: number, bandwidth: number, sampleRate: number): 
        { x1: number; x2: number; y1: number; y2: number; a1: number; a2: number; sampleRate: number } {
        
        // Calculate IIR filter coefficients for resonant bandpass filter
        const r = Math.exp(-Math.PI * bandwidth / sampleRate); // Damping factor
        const theta = 2 * Math.PI * frequency / sampleRate; // Normalized frequency
        
        const a1 = -2 * r * Math.cos(theta);
        const a2 = r * r;
        
        return { 
            x1: 0, x2: 0, y1: 0, y2: 0, 
            a1, a2, sampleRate 
        };
    }
    
    private processFormantFilter(input: number, state: FilterState): number {
        // SCIENTIFIC MODEL: Second-order IIR filter (difference equation)
        // y[n] = b0*x[n] + b1*x[n-1] + b2*x[n-2] + a1*y[n-1] + a2*y[n-2]
        
        // For bandpass filter centered at formant frequency:
        const b0 = 1 - state.a2;
        const b1 = 0;
        const b2 = -(1 - state.a2);
        
        // Calculate output using IIR filter equation
        const output = b0 * input + 
                      b1 * state.x1 + 
                      b2 * state.x2 + 
                      state.a1 * state.y1 + 
                      state.a2 * state.y2;
        
        // Update filter state
        state.x2 = state.x1;
        state.x1 = input;
        state.y2 = state.y1;
        state.y1 = output;
        
        return output;
    }
}

// Export factory function
export function createPhysicalSoundSynthesizer(
    audioContext: AudioContext | null,
    network?: NeuralNetworkSimulator
): PhysicalSoundSynthesizer {
    return new PhysicalSoundSynthesizer(audioContext, network);
}

// ============================================================================
// ENHANCED PHYSICAL MODELS FOR LANGUAGE SYNTHESIS
// ============================================================================

/**
 * LF Glottal Pulse Model (Liljencrants-Fant, 1995)
 * 
 * Realistic glottal pulse waveform for speech synthesis
 * Mathematical model based on actual vocal fold vibration
 */
export class LFGlottalPulseModel {
    /**
     * Generate LF glottal pulse sample
     * 
     * @param t - Time in seconds
     * @param F0 - Fundamental frequency (Hz)
     * @param E0 - Maximum amplitude
     */
    static generateSample(t: number, F0: number, E0: number): number {
        const T0 = 1 / F0;      // Fundamental period
        const Te = 0.25 * T0;   // Time to maximum (25% of period)
        const Tp = 0.5 * T0;    // Pulse end time (50% of period)
        
        const tMod = t % T0;    // Modulo to period
        
        if (tMod < Te) {
            // Opening phase: sine-squared rise
            return E0 * Math.sin(Math.PI * tMod / Te) ** 2;
        } else if (tMod < Tp) {
            // Closing phase: sine decay
            return -E0 * Math.sin(Math.PI * (tMod - Te) / (2 * (Tp - Te)));
        } else {
            // Closed phase
            return 0;
        }
    }
    
    /**
     * Generate complete LF glottal pulse buffer
     */
    static generateBuffer(duration: number, sampleRate: number, F0: number, E0: number): Float32Array {
        const bufferLength = Math.floor(sampleRate * duration);
        const buffer = new Float32Array(bufferLength);
        const dt = 1 / sampleRate;
        
        for (let i = 0; i < bufferLength; i++) {
            buffer[i] = this.generateSample(i * dt, F0, E0);
        }
        
        return buffer;
    }
}

/**
 * Formant Transition Model
 * 
 * Implements coarticulation effects with smooth formant transitions
 */
export class FormantTransitionModel {
    /**
     * Calculate smooth formant transition using logistic function
     * 
     * @param t - Current time (0 to 1)
     * @param startValue - Starting formant frequency
     * @param endValue - Ending formant frequency
     * @param steepness - Transition steepness (default: 8)
     */
    static smoothTransition(t: number, startValue: number, endValue: number, steepness: number = 8): number {
        // Clamp t to 0-1
        t = Math.max(0, Math.min(1, t));
        
        // Logistic sigmoid function for S-curve
        const sigmoid = 1 / (1 + Math.exp(-steepness * (t - 0.5)));
        
        return startValue + (endValue - startValue) * sigmoid;
    }
    
    /**
     * Generate formant trajectory with coarticulation
     * 
     * @param phonemes - Array of phoneme formant targets
     * @param duration - Duration per phoneme (seconds)
     * @param sampleRate - Audio sample rate
     * @param coarticulationStrength - Strength of coarticulation (0-1)
     */
    static generateTrajectory(
        phonemes: Array<{ f1: number; f2: number; f3: number }>,
        duration: number,
        sampleRate: number,
        coarticulationStrength: number = 0.3
    ): Array<{ f1: number; f2: number; f3: number }> {
        const trajectory: Array<{ f1: number; f2: number; f3: number }> = [];
        const samplesPerPhoneme = Math.floor(sampleRate * duration);
        
        for (let p = 0; p < phonemes.length; p++) {
            const current = phonemes[p];
            const previous = phonemes[Math.max(0, p - 1)];
            const next = phonemes[Math.min(phonemes.length - 1, p + 1)];
            
            for (let i = 0; i < samplesPerPhoneme; i++) {
                const progress = i / samplesPerPhoneme;
                
                // Calculate coarticulated formant values
                const f1 = this.smoothTransition(progress,
                    previous.f1 * coarticulationStrength + current.f1 * (1 - coarticulationStrength),
                    next.f1 * coarticulationStrength + current.f1 * (1 - coarticulationStrength)
                );
                
                const f2 = this.smoothTransition(progress,
                    previous.f2 * coarticulationStrength + current.f2 * (1 - coarticulationStrength),
                    next.f2 * coarticulationStrength + current.f2 * (1 - coarticulationStrength)
                );
                
                const f3 = this.smoothTransition(progress,
                    previous.f3 * coarticulationStrength + current.f3 * (1 - coarticulationStrength),
                    next.f3 * coarticulationStrength + current.f3 * (1 - coarticulationStrength)
                );
                
                trajectory.push({ f1, f2, f3 });
            }
        }
        
        return trajectory;
    }
}

/**
 * Consonant Synthesis Models
 */
export class ConsonantSynthesizer {
    /**
     * Synthesize stop consonant burst (plosive)
     * 
     * @param place - Place of articulation ('bilabial', 'alveolar', 'velar')
     * @param voicing - 'voiced' or 'voiceless'
     * @param duration - Duration in seconds
     * @param sampleRate - Audio sample rate
     */
    static synthesizeStop(
        place: string,
        voicing: 'voiced' | 'voiceless',
        duration: number,
        sampleRate: number
    ): Float32Array {
        const bufferLength = Math.floor(sampleRate * duration);
        const buffer = new Float32Array(bufferLength);
        
        // VOT: Voice Onset Time (ms)
        let VOT: number;
        switch (place) {
            case 'bilabial':
                VOT = voicing === 'voiced' ? -20 : 50;  // ms
                break;
            case 'alveolar':
                VOT = voicing === 'voiced' ? -20 : 70;  // ms
                break;
            case 'velar':
                VOT = voicing === 'voiced' ? -20 : 90;  // ms
                break;
            default:
                VOT = voicing === 'voiced' ? -20 : 70;
        }
        
        // Burst characteristics based on place
        const burstFreqs = this.getBurstFrequencies(place);
        
        // Generate burst (first 3ms)
        const burstDuration = Math.min(0.003, duration);
        const burstSamples = Math.floor(sampleRate * burstDuration);
        
        for (let i = 0; i < burstSamples && i < bufferLength; i++) {
            const t = i / sampleRate;
            
            // White noise with spectral shaping
            const noise = (Math.random() * 2 - 1);
            
            // Emphasize burst frequencies based on place
            let sample = noise;
            for (const freq of burstFreqs) {
                sample += noise * 0.2 * Math.sin(freq * t * Math.PI * 2);
            }
            
            // Exponential decay envelope
            const envelope = Math.exp(-t * 200);
            sample *= envelope;
            
            buffer[i] = sample;
        }
        
        // Generate voicing after VOT
        if (voicing === 'voiced' && VOT < 0) {
            // Prevoicing (voicing starts before burst)
            const prevoicingStart = Math.floor(sampleRate * Math.abs(VOT) / 1000);
            for (let i = 0; i < prevoicingStart && i < bufferLength; i++) {
                const t = i / sampleRate;
                const F0 = 120;  // Fundamental frequency
                buffer[i] += 0.3 * Math.sin(F0 * t * Math.PI * 2);
            }
        } else if (voicing === 'voiced' && VOT > 0) {
            // Voicing starts after burst
            const voicingStart = Math.floor(sampleRate * VOT / 1000);
            const F0 = 120;
            
            for (let i = voicingStart; i < bufferLength; i++) {
                const t = i / sampleRate;
                buffer[i] += 0.2 * Math.sin(F0 * t * Math.PI * 2);
            }
        }
        
        return buffer;
    }
    
    /**
     * Synthesize fricative consonant
     * 
     * @param place - Place of articulation
     * @param voicing - 'voiced' or 'voiceless'
     * @param duration - Duration in seconds
     * @param sampleRate - Audio sample rate
     */
    static synthesizeFricative(
        place: string,
        voicing: 'voiced' | 'voiceless',
        duration: number,
        sampleRate: number
    ): Float32Array {
        const bufferLength = Math.floor(sampleRate * duration);
        const buffer = new Float32Array(bufferLength);
        
        // Fricative characteristics
        const spectralProfile = this.getFricativeSpectrum(place);
        
        for (let i = 0; i < bufferLength; i++) {
            const t = i / sampleRate;
            
            // Generate 1/f² turbulent noise
            const whiteNoise = (Math.random() * 2 - 1);
            
            // Apply spectral shaping based on place
            let sample = whiteNoise;
            for (const component of spectralProfile) {
                sample += whiteNoise * component.gain * 
                          Math.sin(component.frequency * t * Math.PI * 2);
            }
            
            // High-pass filter effect (emphasize high frequencies)
            const highpass = 1 + 0.5 * Math.tanh((t - 0.01) * 200);
            sample *= highpass;
            
            buffer[i] = sample * 0.3;
        }
        
        // Add voicing for voiced fricatives
        if (voicing === 'voiced') {
            for (let i = 0; i < bufferLength; i++) {
                const t = i / sampleRate;
                const F0 = 120;
                buffer[i] += 0.15 * Math.sin(F0 * t * Math.PI * 2);
            }
        }
        
        return buffer;
    }
    
    /**
     * Synthesize nasal consonant (/m/, /n/, /ŋ/)
     * 
     * @param place - Place of articulation
     * @param duration - Duration in seconds
     * @param sampleRate - Audio sample rate
     */
    static synthesizeNasal(
        place: string,
        duration: number,
        sampleRate: number
    ): Float32Array {
        const bufferLength = Math.floor(sampleRate * duration);
        const buffer = new Float32Array(bufferLength);
        
        // Nasal formant (around 300 Hz, wide bandwidth)
        const nasalFormant = 300;
        const nasalBandwidth = 300;
        
        const F0 = 120;  // Fundamental frequency
        const coupling = 0.4;  // Nasal coupling strength
        
        for (let i = 0; i < bufferLength; i++) {
            const t = i / sampleRate;
            
            // Generate glottal pulse
            let sample = LFGlottalPulseModel.generateSample(t, F0, 1.0);
            
            // Add nasal resonance (second-order filter)
            const r = Math.exp(-Math.PI * nasalBandwidth / sampleRate);
            const theta = 2 * Math.PI * nasalFormant / sampleRate;
            
            // Simplified IIR filter for nasal formant
            const oralPart = sample * (1 - coupling);  // Oral tract (clamped)
            
            // Nasal resonance (simplified)
            const nasalResonance = sample * coupling * 
                (1 + r * Math.sin(theta * i));
            
            buffer[i] = (oralPart + nasalResonance) * 0.8;
        }
        
        return buffer;
    }
    
    private static getBurstFrequencies(place: string): number[] {
        switch (place) {
            case 'bilabial':
                return [400, 600];  // Low frequencies
            case 'alveolar':
                return [2500, 3500];  // Mid-high frequencies
            case 'velar':
                return [3000, 4500];  // High frequencies
            default:
                return [2000];
        }
    }
    
    private static getFricativeSpectrum(place: string): Array<{ frequency: number; gain: number }> {
        switch (place) {
            case 'labiodental':  // /f/, /v/
                return [
                    { frequency: 1000, gain: 0.1 },
                    { frequency: 2000, gain: 0.15 }
                ];
            case 'dental':  // /θ/, /ð/
                return [
                    { frequency: 2000, gain: 0.2 },
                    { frequency: 4000, gain: 0.15 }
                ];
            case 'alveolar':  // /s/, /z/
                return [
                    { frequency: 4000, gain: 0.4 },
                    { frequency: 6000, gain: 0.3 },
                    { frequency: 8000, gain: 0.25 }
                ];
            case 'postalveolar':  // /ʃ/, /ʒ/
                return [
                    { frequency: 3000, gain: 0.3 },
                    { frequency: 4500, gain: 0.25 },
                    { frequency: 6000, gain: 0.2 }
                ];
            default:
                return [{ frequency: 2000, gain: 0.2 }];
        }
    }
}

/**
 * F0 Contour Generator for Prosody
 */
export class F0ContourGenerator {
    /**
     * Generate F0 contour for intonation patterns
     * 
     * @param type - Type of intonation
     * @param duration - Duration in seconds
     * @param sampleRate - Audio sample rate
     * @param baseF0 - Base fundamental frequency (Hz)
     */
    static generateContour(
        type: 'declarative' | 'interrogative' | 'emphatic' | 'flat',
        duration: number,
        sampleRate: number,
        baseF0: number = 150
    ): Float32Array {
        const bufferLength = Math.floor(sampleRate * duration);
        const contour = new Float32Array(bufferLength);
        
        for (let i = 0; i < bufferLength; i++) {
            const t = i / sampleRate;
            const progress = t / duration;
            
            let f0 = baseF0;
            
            switch (type) {
                case 'declarative':
                    // Falling pitch
                    f0 -= 30 * progress;
                    break;
                    
                case 'interrogative':
                    // Rising pitch
                    f0 += 40 * (1 - Math.exp(-5 * progress));
                    break;
                    
                case 'emphatic':
                    // Peak at 30% through, then fall
                    const peak = 0.3;
                    if (progress < peak) {
                        f0 += 50 * (progress / peak);
                    } else {
                        f0 += 50 * (1 - (progress - peak) / (1 - peak));
                    }
                    break;
                    
                case 'flat':
                    // Flat line
                    break;
            }
            
            contour[i] = f0;
        }
        
        return contour;
    }
}

